import "../../stylesheets/App.css";
import "../../stylesheets/Game.css";
import React, { useState, useEffect, useMemo } from "react";
import PopupAdminStages from "./PopupAdminStages.js";
import LoaderGame from "./LoaderGame.js";
import PopupError from "./PopupError.js";

function GameAdmin(props) {
  const backgroundDefault = useMemo(
    () => ({
      backgroundImage:
        "url(" + process.env.PUBLIC_URL + "/images/assets/Pais_default.png)",
    }),
    []
  );

  const [loadingSaveTeams, setLoadingSaveTeams] = useState(false);
  const [loadingSaveScores, setLoadingSaveScores] = useState(false);

  const [score1, setScore1] = useState("");
  const [score2, setScore2] = useState("");
  const [initialScore1, setInitialScore1] = useState("");
  const [initialScore2, setInitialScore2] = useState("");

  const [team1, setTeam1] = useState(null);
  const [team2, setTeam2] = useState(null);
  const [initialTeam1, setInitialTeam1] = useState(null);
  const [initialTeam2, setInitialTeam2] = useState(null);

  const [background1, setBackground1] = useState(backgroundDefault);
  const [background2, setBackground2] = useState(backgroundDefault);

  const [openScorePopup, setOpenScorePopup] = useState(false);
  const [popupSaveTeams, setPopupSaveTeams] = useState(false);
  const [popupSaveScore, setPopupSaveScore] = useState(false);

  const [openErrorTeamsPopup, setOpenErrorTeamsPopup] = useState(false);
  const [openErrorScorePopup, setOpenErrorScorePopup] = useState(false);

  // --- Inicializa data del juego (teams + scores)
  useEffect(() => {
    const teams = props.data?.teams || [];
    if (teams.length === 2) {
      const ordered = [...teams].sort(
        (a, b) => a.GameTeam.order - b.GameTeam.order
      );

      const t1 = ordered[0];
      const t2 = ordered[1];

      setScore1(t1.GameTeam.score ?? "");
      setScore2(t2.GameTeam.score ?? "");
      setInitialScore1(t1.GameTeam.score ?? "");
      setInitialScore2(t2.GameTeam.score ?? "");

      setTeam1(t1);
      setTeam2(t2);
      setInitialTeam1(t1);
      setInitialTeam2(t2);

      setBackground1({ backgroundImage: "url(" + t1.image_url + ")" });
      setBackground2({ backgroundImage: "url(" + t2.image_url + ")" });
    }
  }, [props.data?.teams, props.data?.idGame]);

  // ✅ Bloquea teclas inválidas en type="number"
  const blockInvalidNumberKeys = (e) => {
    const invalid = ["-", "+", "e", "E", ".", ","];
    if (invalid.includes(e.key)) e.preventDefault();
  };

  // ✅ Limpia todo lo que no sea dígito (evita -1, etc.)
  const updateScore1 = (e) => {
    const cleaned = String(e.currentTarget.value).replace(/[^\d]/g, "");
    setScore1(cleaned);
  };

  const updateScore2 = (e) => {
    const cleaned = String(e.currentTarget.value).replace(/[^\d]/g, "");
    setScore2(cleaned);
  };

  const hasScoreChanges =
    score1 !== "" &&
    score2 !== "" &&
    (score1 != initialScore1 || score2 != initialScore2);

  const hasTeamChanges =
    team1 &&
    team2 &&
    team1.idTeam != team2.idTeam &&
    (team1.idTeam != initialTeam1?.idTeam || team2.idTeam != initialTeam2?.idTeam);

  const updateTeam1 = (e) => {
    const id = e.currentTarget.value;
    const actTeam = props.teamList?.find((t) => String(t.idTeam) === String(id));

    if (!actTeam) {
      setTeam1(null);
      setBackground1(backgroundDefault);
      return;
    }

    setTeam1(actTeam);
    setBackground1({ backgroundImage: "url(" + actTeam.image_url + ")" });
  };

  const updateTeam2 = (e) => {
    const id = e.currentTarget.value;
    const actTeam = props.teamList?.find((t) => String(t.idTeam) === String(id));

    if (!actTeam) {
      setTeam2(null);
      setBackground2(backgroundDefault);
      return;
    }

    setTeam2(actTeam);
    setBackground2({ backgroundImage: "url(" + actTeam.image_url + ")" });
  };

  const restoreTeams = () => {
    const t1 = props.teamList?.find((t) => t.idTeam == initialTeam1?.idTeam);
    const t2 = props.teamList?.find((t) => t.idTeam == initialTeam2?.idTeam);

    setBackground1(
      t1 ? { backgroundImage: "url(" + t1.image_url + ")" } : backgroundDefault
    );
    setBackground2(
      t2 ? { backgroundImage: "url(" + t2.image_url + ")" } : backgroundDefault
    );
  };

  const saveScore = async () => {
    setOpenScorePopup(false);
    setLoadingSaveScores(true);

    try {
      let result = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/saveScore/${props.data.idGame}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ score1, score2 }),
        }
      );

      result = await result.json();

      if (result.status == 200) {
        setPopupSaveScore(true);
        setTimeout(() => setPopupSaveScore(false), 2000);
        setInitialScore1(score1);
        setInitialScore2(score2);
      } else if (result.status == 201) {
        setScore1(initialScore1);
        setScore2(initialScore2);
        setOpenErrorScorePopup(true);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingSaveScores(false);
    }
  };

  const saveTeams = async () => {
    if (!team1 || !team2) return;

    setLoadingSaveTeams(true);

    try {
      let result = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/saveTeams/${props.data.idGame}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            team1: team1.idTeam,
            team2: team2.idTeam,
          }),
        }
      );

      result = await result.json();

      if (result.status == 200) {
        setPopupSaveTeams(true);
        setTimeout(() => setPopupSaveTeams(false), 2000);
        setInitialTeam1(team1);
        setInitialTeam2(team2);
      } else if (result.status == 201) {
        setTeam1(initialTeam1);
        setTeam2(initialTeam2);
        restoreTeams();
        setOpenErrorTeamsPopup(true);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingSaveTeams(false);
    }
  };

  // Formato fecha/hora
  const fechaOriginal = new Date(props.data.date);
  const dia = String(fechaOriginal.getDate()).padStart(2, "0");
  const mes = String(fechaOriginal.getMonth() + 1).padStart(2, "0");
  const anio = fechaOriginal.getFullYear();
  const fechaFormateada = `${dia}/${mes}/${anio}`;

  const horas = String(fechaOriginal.getHours()).padStart(2, "0");
  const minutos = String(fechaOriginal.getMinutes()).padStart(2, "0");
  const horaFormateada = `${horas}:${minutos} hs`;

  return (
    <>
      <div className="game-container admin-game-container">
        <div className="team-container">
          <div className="team-img" style={background1} />
          <div className="admin-team-container-full-width">
            <div className="admin-team-container score-imput">
              <select value={team1?.idTeam || -1} onChange={updateTeam1}>
                <option value={-1} hidden>
                  Seleccionar
                </option>
                {props.teamList?.map((team) => (
                  <option key={team.idTeam} value={team.idTeam}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="input-container">
            <input
              type="number"
              min={0}
              step={1}
              className="score-imput"
              placeholder="-"
              value={score1}
              onKeyDown={blockInvalidNumberKeys}
              onChange={updateScore1}
            />
          </div>
        </div>

        <div className="date-container">
          <p>{props.data.stage.name}</p>
          <p>{fechaFormateada}</p>
          <p>{horaFormateada}</p>

          <div className="save-container save-teams-container">
            <button
              className="save-btn save-teams-btn"
              id={"save-teams-btn-" + props.data.idGame}
              onClick={saveTeams}
              disabled={!hasTeamChanges}
              style={{
                backgroundColor: hasTeamChanges
                  ? "var(--color-verde)"
                  : "var(--color-gris-claro-fuente)",
              }}
            >
              Guardar Equipos
            </button>
          </div>

          <div className="save-container">
            <button
              className="save-btn"
              id={"save-btn-" + props.data.idGame}
              onClick={() => setOpenScorePopup(true)}
              disabled={!hasScoreChanges}
              style={{
                backgroundColor: hasScoreChanges
                  ? "var(--color-verde)"
                  : "var(--color-gris-claro-fuente)",
              }}
            >
              Guardar Resultado
            </button>
          </div>
        </div>

        <div className="team-container">
          <div className="team-img" style={background2} />
          <div className="admin-team-container-full-width">
            <div className="admin-team-container score-imput">
              <select value={team2?.idTeam || -1} onChange={updateTeam2}>
                <option value={-1} hidden>
                  Seleccionar
                </option>
                {props.teamList?.map((team) => (
                  <option key={team.idTeam} value={team.idTeam}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="input-container">
            <input
              type="number"
              min={0}
              step={1}
              className="score-imput"
              placeholder="-"
              value={score2}
              onKeyDown={blockInvalidNumberKeys}
              onChange={updateScore2}
            />
          </div>
        </div>

        {popupSaveTeams ? (
          <div className="popup-bet">Encuentro agendado con éxito</div>
        ) : null}
        {popupSaveScore ? (
          <div className="popup-bet">Resultado registrado con éxito</div>
        ) : null}

        {openScorePopup ? (
          <PopupAdminStages
            margin_0={true}
            title="¿Confirmar resultado?"
            text="El resultado ingresado repartirá los puntos a todos los usuarios"
            action={saveScore}
            closeAction={setOpenScorePopup}
            closeValue={false}
          />
        ) : null}

        {openErrorScorePopup ? (
          <PopupError
            description="Este partido aun no se ha jugado, no puedes guardar su resultado. Lo siento!"
            setOpenErrorPopup={setOpenErrorScorePopup}
            setRefresh={props.setRefresh}
          />
        ) : null}

        {openErrorTeamsPopup ? (
          <PopupError
            description="Este partido ya se ha jugado, no puedes cambiar los equipos. Lo siento!"
            setOpenErrorPopup={setOpenErrorTeamsPopup}
            setRefresh={props.setRefresh}
          />
        ) : null}

        {loadingSaveScores || loadingSaveTeams ? <LoaderGame /> : null}
      </div>
    </>
  );
}

export default GameAdmin;
